/**
 * 同期処理(CLAUDE.md §4-2, §4-4)
 *
 * 原則:
 *
 *  - 参加者に通信エラーを見せない。失敗は黙って再試行キューに戻す。
 *    唯一の例外が設定・ヘルプ画面の同期状態表示。
 *  - UIをブロックしない。呼び出し側は待たない。
 *  - 同意が得られていない端末は一切送らない(§14)。「まだ聞いていない」も
 *    「拒否された」も同じ扱いにする。未回答を送信可とすると、QRから
 *    直接来て同意画面を一度も見ていない参加者のデータを黙って送ることになる。
 *
 * 送信トリガはスタンプ取得時・アプリ復帰時・オンライン復帰時・達成時。
 * 失敗時は指数バックオフで間隔を空ける。島内では圏外のまま何時間も経つので、
 * 短い間隔で叩き続けても電池を消費するだけになる。
 */

import { getDeviceId, getSyncConsent } from "./prefs";
import {
  dequeue,
  getQueue,
  type ProofQueueItem,
  type StampQueueItem,
} from "./sync-queue";
import { getConnState, probe } from "./net";

export type SyncState = "idle" | "syncing" | "waiting";

export interface SyncStatus {
  state: SyncState;
  /** 送信待ちの件数 */
  pending: number;
  lastSuccessAt: number | null;
  /** 同意が得られていないため送信しない(未回答・拒否のいずれも) */
  disabled: boolean;
  /** 同意をまだ聞いていない */
  unanswered: boolean;
}

/** 1リクエストで送る最大件数。サーバー側の上限(50)より余裕をもたせる。 */
const BATCH_SIZE = 20;

const BACKOFF_BASE_MS = 5_000;
const BACKOFF_MAX_MS = 5 * 60_000;

/**
 * 再送しても結果が変わらない拒否理由。
 * これらはキューから外す。残すと永久に再試行し続けるだけになる。
 */
const PERMANENT_REJECTIONS = new Set(["bad_token", "malformed", "unknown_cp"]);

let running = false;
let failures = 0;
let nextAttemptAt = 0;
let lastSuccessAt: number | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;

const listeners = new Set<(s: SyncStatus) => void>();

export function onSyncChange(fn: (s: SyncStatus) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export async function getSyncStatus(): Promise<SyncStatus> {
  const consent = await getSyncConsent();
  return {
    state: running ? "syncing" : nextAttemptAt > Date.now() ? "waiting" : "idle",
    pending: (await getQueue()).length,
    lastSuccessAt,
    disabled: consent !== true,
    unanswered: consent === null,
  };
}

async function emit(): Promise<void> {
  if (listeners.size === 0) return;
  const status = await getSyncStatus();
  for (const fn of listeners) fn(status);
}

/**
 * キューを送る。呼び出し側は結果を待たなくてよい。
 * 何を投げても例外にはしない。
 */
export async function syncNow(force = false): Promise<void> {
  if (running) return;
  if (!force && Date.now() < nextAttemptAt) return;

  // 同意が得られていなければ送らない。キューは残す(あとで同意されたら送る)。
  if ((await getSyncConsent()) !== true) return;

  const queue = await getQueue();
  if (queue.length === 0) return;

  // navigator.onLine が true でも通じないことが多い島内では、
  // 実疎通が取れてから初めて送る(§4-3)。
  if (getConnState() !== "online" && !(await probe())) return;

  running = true;
  void emit();

  try {
    const deviceId = await getDeviceId();

    const stampItems = queue.filter((q): q is StampQueueItem => q.type === "stamp");
    const proofItems = queue.filter((q): q is ProofQueueItem => q.type === "proof");

    for (let i = 0; i < stampItems.length; i += BATCH_SIZE) {
      if (!(await sendBatch(deviceId, stampItems.slice(i, i + BATCH_SIZE)))) {
        scheduleRetry();
        return;
      }
    }

    for (const item of proofItems) {
      if (!(await sendProof(deviceId, item))) {
        scheduleRetry();
        return;
      }
    }

    failures = 0;
    nextAttemptAt = 0;
    lastSuccessAt = Date.now();
  } catch {
    scheduleRetry();
  } finally {
    running = false;
    void emit();
  }
}

async function sendBatch(deviceId: string, batch: StampQueueItem[]): Promise<boolean> {
  let res: Response;
  try {
    res = await fetch("/api/stamps", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        device_id: deviceId,
        items: batch.map((b) => b.payload),
      }),
      // 同期は常にバックグラウンド。キャッシュを一切挟まない。
      cache: "no-store",
    });
  } catch {
    return false; // 圏外・瞬断。黙って再試行へ。
  }

  if (res.status >= 500 || res.status === 429) return false;

  if (!res.ok) {
    // 4xx は送り直しても直らない。キューを詰まらせないよう捨てる。
    console.warn("[sync] サーバーがリクエストを拒否しました", res.status);
    for (const item of batch) await dequeue(item.ulid);
    return true;
  }

  const body = (await res.json().catch(() => null)) as {
    accepted?: string[];
    rejected?: { ulid: string; reason: string }[];
  } | null;

  const accepted = new Set(body?.accepted ?? []);
  for (const item of batch) {
    if (accepted.has(item.ulid)) {
      await dequeue(item.ulid);
      continue;
    }
    const reason = body?.rejected?.find((r) => r.ulid === item.ulid)?.reason;
    if (reason && PERMANENT_REJECTIONS.has(reason)) {
      console.warn(`[sync] ${item.ulid} を破棄しました: ${reason}`);
      await dequeue(item.ulid);
    }
  }
  return true;
}

/**
 * 達成証明をサーバーに登録する。
 * 受付で8文字だけ入力されたときの照合相手になる(§9-5)。
 */
async function sendProof(deviceId: string, item: ProofQueueItem): Promise<boolean> {
  let res: Response;
  try {
    res = await fetch("/api/proof", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ device_id: deviceId, ...item.payload }),
      cache: "no-store",
    });
  } catch {
    return false;
  }

  if (res.status >= 500 || res.status === 429) return false;

  // 4xx は送り直しても直らない。登録できなくても、QRを見せれば
  // 受付はオフラインで検証できる(§9-5)ので参加者への影響はない。
  if (!res.ok) console.warn("[sync] 達成証明を登録できませんでした", res.status);

  await dequeue(item.ulid);
  return true;
}

function scheduleRetry(): void {
  failures++;
  const base = Math.min(BACKOFF_BASE_MS * 2 ** (failures - 1), BACKOFF_MAX_MS);
  // ジッタ。復帰の瞬間に全端末が一斉に叩くのを避ける。
  const jitter = base * (0.8 + Math.random() * 0.4);
  nextAttemptAt = Date.now() + jitter;

  if (timer) clearTimeout(timer);
  timer = setTimeout(() => void syncNow(true), jitter);
  void emit();
}

/** 同期のトリガを仕掛ける。アプリ起動時に一度だけ呼ぶ(§4-4)。 */
export function startSync(): void {
  addEventListener("online", () => void syncNow(true));

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void syncNow();
  });

  void syncNow(true);
}

/** スタンプ取得・達成など、送るべきことが起きたときに呼ぶ */
export function requestSync(): void {
  void syncNow(true);
}
